import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import users from "../../data/users";
import env from '../../config';
import axios from 'axios';

const ExtraServiceDetails = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const [project, setProject] = useState({}); // estado para armazenar os dados do projeto
  const [executedPhotos, setExecutedPhotos] = useState([]); // estado para armazenar as fotos executadas
  const [referencePhotos, setReferencePhotos] = useState([]); // estado para armazenar as fotos executadas
  const location = useLocation();
  let stylesLoaded = false;
  const backendUrl = env.backendUrl;
  const url = backendUrl;
  const navigate = useNavigate(); // cria uma instância do navigate
  const [selectedImage, setSelectedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const userId = localStorage.getItem('userId'); // recupera o userId do LocalStorage
  const user = users.find((u) => u.id === parseInt(userId));
  const [isCompleted, setIsCompleted] = useState(false);

  useEffect(() => {
    const isTailwindRoute = location.pathname.startsWith('/extraService-details');
    if (isTailwindRoute && !stylesLoaded) {
      import('../../tailwind.css')
        .then(() => {
          stylesLoaded = true;
        })
        .catch(error => {
          console.error('Erro ao carregar estilos do Timesheet:', error);
        });
    }
  }, [location]);

  useEffect(() => {
    // busca na API pelo id do projeto
    fetch(url + `/extra-services/${id}`)
      .then(response => response.json())
      .then(data => {
        setProject(data.data);
        setIsCompleted(!!data.data.execution_date);
        // Verifica se executed_photos é uma string JSON e converte se necessário
        const executedPhotos = typeof data.data.executed_photos === 'string'
          ? JSON.parse(data.data.executed_photos)
          : [];
        setExecutedPhotos(executedPhotos);

        const referencePhotos = typeof data.data.reference_photos === 'string'
          ? JSON.parse(data.data.reference_photos)
          : [];
        setReferencePhotos(referencePhotos);
      })
      .catch(error => console.error('Erro ao buscar projeto:', error));
  }, [id]);


  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setIsUploading(true);
    setUploadError('');

    const uploadPromises = files.map((file) => {
      const formData = new FormData();
      formData.append('image', file);

      return axios.post(url + `/api/upload-image-drive`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
        .then((response) => response.data.path)
        .catch((error) => {
          console.error(`Erro ao fazer upload da imagem ${file.name}:`, error);
          throw new Error(`Error uploading ${file.name}`);
        });
    });

    Promise.all(uploadPromises)
      .then((uploadedPaths) => {
        const updatedPhotos = [...executedPhotos, ...uploadedPaths];

        axios.put(url + `/extra-services/${id}`, {
          executed_photos: updatedPhotos,
        })
          .then((response) => {
            console.log(response.data);
            setExecutedPhotos(updatedPhotos);
          })
          .catch((error) => {
            console.error(error);
            setUploadError('Error saving photos to service');
          })
          .finally(() => {
            setIsUploading(false);
          });
      })
      .catch((error) => {
        console.error(error);
        setUploadError(error.message || 'Error uploading one or more photos');
        setIsUploading(false);
      });
  };

  const handleRemovePhoto = (index) => {
    const confirmDelete = window.confirm('Are you sure?');
    if (!confirmDelete) return;

    const photoToDelete = executedPhotos[index];
    console.log('Photo URL to delete:', photoToDelete);

    const updatedPhotos = executedPhotos.filter((_, i) => i !== index);

    // Extract fileId from Google Drive URL
    // Format: https://drive.google.com/thumbnail?id=FILEID&sz=w1000
    const fileId = photoToDelete.match(/id=([^&]+)/)?.[1];
    console.log('Extracted file ID:', fileId);
    
    if (!fileId) {
      console.error('Could not extract file ID from URL');
      return;
    }

    // First delete from Google Drive
    setIsUploading(true);
    console.log('Sending delete request with fileId:', fileId);
    axios.delete(url + '/api/images/google-drive', {
      data: { fileId: fileId }
    })
      .then(() => {
        // After successful deletion from Google Drive, update the database
        return axios.put(url + `/extra-services/${id}`, {
          executed_photos: updatedPhotos,
        });
      })
      .then((response) => {
        console.log('Photos updated successfully:', response.data);
        setExecutedPhotos(updatedPhotos); // Update locally
        setIsUploading(false);
      })
      .catch((error) => {
        console.error('Error removing photo:', error);
        
        // If the file is not found (404) or there's another error with Google Drive,
        // we still want to update the database to remove the reference
        if (error.response && (error.response.status === 404 || error.response.data?.error?.includes('Google Drive'))) {
          console.log('File not found in Google Drive or other Google Drive error. Updating database anyway...');
          return axios.put(url + `/extra-services/${id}`, {
            executed_photos: updatedPhotos,
          }).then(() => {
            setExecutedPhotos(updatedPhotos);
            setIsUploading(false);
          });
        }
        
        alert('Error removing photo. Please try again.');
        setIsUploading(false);
      });
  };

  const handleBack = () => {
    navigate(`/extraService-list?userId=${userId}`);
  };

  const handleCheckboxChange = async (e) => {
    const checked = e.target.checked;
    const execution_date = checked ? new Date().toISOString().split('T')[0] : null;

    try {
      await axios.put(`${url}/extra-services/${id}`, {
        execution_date: execution_date,
        request_status: checked ? 'completed' : 'pending',
      });

      setIsCompleted(checked);
      setProject((prev) => ({ ...prev, execution_date }));
    } catch (error) {
      console.error('Erro ao atualizar execution_date:', error);
    }
  };

  return (
    <div className="max-w-md mx-auto p-4 space-y-4 bg-white min-h-screen">
      <h2 className="text-xl font-bold text-center">Service Details</h2>

      <div className="bg-white rounded-2xl shadow p-4 space-y-2">
        <div className="text-sm space-y-2">
          <div>
            <p className="text-gray-500 text-xs">Request Date</p>
            <p className="font-medium">{project.request_date}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs">Service Address</p>
            <p className="font-medium">{project.service_address}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs">Description</p>
            <p className="font-medium">{project.service_description}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow p-4">
        <h3 className="text-sm font-semibold mb-3">Reference Photos:</h3>
        <div className="flex flex-wrap gap-3">
          {referencePhotos && referencePhotos.map((src, index) => (
            <div key={index} className="relative w-20 h-20">
              <img src={src}  
                onClick={() => setSelectedImage(src)}
                referrerPolicy="no-referrer"
                alt={`Uploaded ${index + 1}`} className="w-full h-full object-cover rounded-lg" />
              {isUploading && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-50">
                  <div className="flex flex-col items-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent"></div>
                    <p className="text-white mt-4 text-lg font-semibold">Uploading...</p>
                  </div>
                </div>
              )}

              {uploadError && (
                <div className="fixed bottom-5 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg z-50">
                  {uploadError}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow p-4">
        <h3 className="text-sm font-semibold mb-3">Uploaded Photos:</h3>
        <div className="flex flex-wrap gap-3">
          <label
            className={`flex flex-col items-center justify-center w-20 h-20 border border-dashed rounded-lg cursor-pointer text-center text-xs ${isUploading ? 'border-gray-300 text-gray-300 cursor-not-allowed' : 'border-gray-400 text-gray-500'
              }`}
          >
            {isUploading ? 'Uploading...' : (
              <>
                +<br />Add Photo
              </>
            )}
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handlePhotoUpload}
              disabled={isUploading}
            />
          </label>

          {executedPhotos && executedPhotos.map((src, index) => (
            <div key={index} className="relative w-20 h-20">
              <img src={src}  
                onClick={() => setSelectedImage(src)}
                referrerPolicy="no-referrer"
                alt={`Uploaded ${index + 1}`} className="w-full h-full object-cover rounded-lg" />
              <button
                className="absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full w-5 h-5 text-xs text-black shadow"
                onClick={() => handleRemovePhoto(index)}
              >
                ×
              </button>
              {isUploading && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-50">
                  <div className="flex flex-col items-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent"></div>
                    <p className="text-white mt-4 text-lg font-semibold">Uploading...</p>
                  </div>
                </div>
              )}

              {uploadError && (
                <div className="fixed bottom-5 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg z-50">
                  {uploadError}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-center">
        {user?.id === 0 && (
          <button
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded ml-4 mr-4"
            onClick={handleBack}
          >
            Back
          </button>
        )}

        <label className="relative inline-flex items-center justify-center cursor-pointer">
          <input type="checkbox" className="sr-only peer" checked={isCompleted} onChange={handleCheckboxChange} />
          <div className="w-16 h-9 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-400 rounded-full 
                        peer peer-checked:after:translate-x-full peer-checked:after:border-white 
                        after:content-[''] after:absolute after:top-[4px] after:left-[2px] 
                        after:bg-white after:border-gray-300 after:border after:rounded-full 
                        after:h-7 after:w-7 after:transition-all peer-checked:bg-green-500"></div>
          <span className="ml-3 text-sm font-medium text-gray-900">
            {isCompleted ? 'Completed' : 'Pending'}
          </span>
        </label>
      </div>

      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="relative">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-4 -right-4 bg-white text-black rounded-full w-8 h-8 flex items-center justify-center shadow-lg hover:bg-gray-200"
              aria-label="Fechar"
            >
              ✕
            </button>

            <img
              src={selectedImage} 
              alt="Zoomed"
              referrerPolicy="no-referrer"
              className="max-w-[90vw] max-h-[90vh] rounded-lg border-4 border-white shadow-xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ExtraServiceDetails;
